import { ChartManager } from './chart.manager';

import { DotTypes, isHorizontalType, isPieType, isTimeType, isVerticalType } from '@23degrees/shared-code/config/constants';
import { D3TextData, d3renderText } from './chart.d3';

import { clamp, cloneObject, roundTo } from '@23degrees/shared-code/utilities';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import { Quill } from '@23degrees/shared-code/models';
import { callMoment, getMoment, momentToStr, objToMoment } from '@23degrees/shared-code/utilities/date';
import chroma from 'chroma-js';
import { group } from 'd3-array';
import { scaleOrdinal } from 'd3-scale';
import { overlayText } from './chart.overlay';
import { updateOverlay, updateSVG } from './chart.utils';

const alignmentToFlex = (alignment, rotation = 'top') => {
  const matrix = {
    top: {
      'top-left': ['flex-start', 'flex-start'],
      'top-center': ['center', 'flex-start'],
      'top-right': ['flex-end', 'flex-start'],
      'center-left': ['flex-start', 'center'],
      center: ['center', 'center'],
      'center-right': ['flex-end', 'center'],
      'bottom-left': ['flex-start', 'flex-end'],
      'bottom-center': ['center', 'flex-end'],
      'bottom-right': ['flex-end', 'flex-end'],
    },
    left: {
      'top-left': ['flex-end', 'flex-start'],
      'top-center': ['flex-end', 'center'],
      'top-right': ['flex-end', 'flex-end'],
      'center-left': ['center', 'flex-start'],
      center: ['center', 'center'],
      'center-right': ['center', 'flex-end'],
      'bottom-left': ['flex-start', 'flex-start'],
      'bottom-center': ['flex-start', 'center'],
      'bottom-right': ['flex-start', 'flex-end'],
    },
  };

  const [justifyContent, alignItems] = matrix[rotation][alignment];
  return { justifyContent, alignItems };
};

// overflow-wrap: break-word;

// hex transitions suck, alpha would need svg fill #rrggbb and fill-opacity #a
const color2css = (color) => chroma(color).hex();

const fixLineHeight = (obj: any) => {
  if (typeof obj === 'number') return obj;
  return 1.25;
};

export const timeTicks = (values, tickNum = 7) => {
  if (values.length <= tickNum) return values;
  const factor = Math.ceil(values.length / tickNum);
  return values.filter((obj, idx) => idx % factor === (values.length + factor - 1) % factor);
};

export const timeScaleTicks = (timeScale, tickNum = 5) => {
  const domain = timeScale.domain().map((obj) => callMoment(obj));
  if (tickNum < 3) return domain;

  const ticks = [domain[0]];
  const absDiff = Math.abs(domain[0].diff(domain[1], 'seconds'));
  const tickPart = Math.ceil(absDiff / (tickNum - 1));

  for (let idx = 1; idx < tickNum - 1; idx++) {
    ticks.push(domain[0].clone().add(tickPart * idx, 'seconds'));
  }

  ticks.push(domain[1]);
  return ticks;
};

let axisValues = [];

export const processOrdinalScale = (chartManager: ChartManager, adjustWidth = false) => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { labelAxis } = typeSpecific.chart.axis;
  const { quillValue, style } = typeSpecific.chart.values;
  const { dynamicHeight, bars, dots } = typeSpecific.chart;
  const { enabled, labelsAboveBars } = labelAxis;

  // populate label data
  if (axisValues.length === 0) {
    const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
    const axisSet = new Set();
    for (const dataRow of chartManager.data) {
      if (axisSet.has(dataRow[mapField])) continue;
      axisSet.add(dataRow[mapField]);
      axisValues.push(dataRow);
    }
  }

  const grouped = type === 'bar-grouped-horizontal';
  // In order to build the yScaleOrdinal, we need pixel positions
  // Get initial item (Bar|Dot) height & paddings (Dynamic or static height) in px
  let itemHeight = 0;
  let paddingOuterPx = 0;
  let paddingInnerPx = 0;
  let groupHeight = 0;
  let groupPaddingInnerPx = 0;
  let groupPaddingOuterPx = 0;

  let useMinHeight = false;

  const H = chartManager.chartHeight;
  const n = axisValues.length;

  if (DotTypes.includes(type)) {
    // Dots
    if (dynamicHeight) {
      const offset = 4;
      const oneRow = chartManager.data[0];
      const valueQuill = chartManager.getHTMLandRect(quillValue, style, oneRow, chartManager.content, true);
      const extraValueQuill = chartManager.getHTMLandRect(dots.extraLabels.quill, style, oneRow, chartManager.content, true);

      itemHeight = 2 * (valueQuill?.height || 0) + dots.size - offset;

      if (chartManager.dataGroup.length <= 2) {
        // Only if chart has only 2 groups or less
        // Adjuts height in case labels are top/bottom
        if (['top', 'bottom'].includes(dots.labelPosition)) {
          itemHeight = 2 * (valueQuill?.height || 0) + dots.size - offset;
        } else if (dots.extraLabels.enabled && ['top', 'bottom'].includes(dots.extraLabels.position)) {
          itemHeight = 2 * (extraValueQuill.height || 0) + dots.size - offset;
        } else {
          itemHeight = Math.max(dots.size, valueQuill?.height || 0, extraValueQuill?.height || 0) + offset;
        }
      }
      // itemHeight = chartManager.dotLaneHeight; // Initial height
      // paddingInnerPx = dots.paddingInnerPx
      // paddingOuterPx = dots.paddingOuterPx
    } else {
      itemHeight = H / (n + (n - 1) * dots.paddingInner + 2 * dots.paddingOuter);

      // 23° Paddings:
      const paddingHeight = (n - 1) * dots.paddingInner + 2 * dots.paddingOuter;
      itemHeight = H / (n + paddingHeight);
    }

    // paddingInnerPx = itemHeight * dots.paddingInner;
    // paddingOuterPx = itemHeight * dots.paddingOuter;

    paddingInnerPx = (n - 1) * dots.paddingInner * itemHeight;
    paddingOuterPx = 2 * dots.paddingOuter * itemHeight;
  } else {
    // Bars
    const g = grouped ? chartManager.dataGroup.length : 1;
    if (dynamicHeight) {
      itemHeight = bars.height;
      groupHeight = itemHeight * g;

      // paddingInnerPx = bars.paddingInnerPx;
      // paddingOuterPx = bars.paddingOuterPx;
      // if(grouped) {
      //   groupHeight = (bars.height * chartManager.dataGroup.length) + (bars.groupPaddingInnerPx * (chartManager.dataGroup.length - 1) + (bars.groupPaddingOuterPx * 2));
      //   groupPaddingInnerPx = bars.groupPaddingInnerPx;
      //   groupPaddingOuterPx = bars.groupPaddingOuterPx;
      // }
    } else {
      // itemHeight = chartManager.yScaleBandWidth() > bars.minHeight ? chartManager.yScaleBandWidth() : bars.minHeight
      // D3 Paddings: (Old way)
      // Find Bar height using % (https://observablehq.com/@hongtaoh/how-to-understand-d3s-paddinginner)

      // Get total label space in case labelsAboveBars
      let labelsAboveOffset = 0;
      if (enabled && labelsAboveBars) {
        for (const dataRow of axisValues) {
          labelsAboveOffset += dataRow['~meta'].labelHeightUpdated;
        }
      }

      // 23° Paddings:
      const paddingHeight = (n - 1) * bars.paddingInner + 2 * bars.paddingOuter;
      const groupPaddingHeight = (g - 1) * bars.groupPaddingInner + 2 * bars.groupPaddingOuter;

      // console.log('paddingHeight', paddingHeight);
      // console.log('groupPaddingHeight', groupPaddingHeight);

      itemHeight = Math.max(bars.minHeight, (H - labelsAboveOffset) / (n + paddingHeight));

      if (grouped) {
        groupHeight = (H - labelsAboveOffset) / (n + paddingHeight);
        itemHeight = Math.max(bars.minHeight, groupHeight / (g + groupPaddingHeight));

        if (groupHeight / (g + groupPaddingHeight) < bars.minHeight) {
          useMinHeight = true;

          // update groupHeight because itemHeight could be affected by bars.minHeight
          groupHeight = itemHeight * g;
        }
      }

      // console.log('itemHeight', itemHeight);
      // console.log('groupHeight', groupHeight);
    }

    // console.log('itemHeight', itemHeight);
    // console.log('bars', bars);

    paddingInnerPx = (n - 1) * bars.paddingInner * itemHeight;
    paddingOuterPx = 2 * bars.paddingOuter * itemHeight;

    groupPaddingInnerPx = bars.groupPaddingInner * itemHeight;
    groupPaddingOuterPx = 2 * bars.groupPaddingOuter * itemHeight;

    if (grouped) {
      paddingInnerPx = (n - 1) * bars.paddingInner * groupHeight;
      paddingOuterPx = 2 * bars.paddingOuter * groupHeight;

      groupPaddingInnerPx = bars.groupPaddingInner * itemHeight;
      groupPaddingOuterPx = 2 * bars.groupPaddingOuter * itemHeight;
    }

    if (dynamicHeight || useMinHeight) {
      // itemHeight += paddingInnerPx;

      if (grouped) {
        groupHeight += (g - 1) * groupPaddingInnerPx;
      }
    }
  }

  // Hardcoded Example
  const clusterLabels = [
    {
      c1: 'Label 1',
      '~meta': {
        labelWidth: 20,
        labelHeight: 15,
        labelWidthUpdated: 20,
        labelHeightUpdated: 15,
        htmlStr:
          '<div style="font-size:11px;font-family:\'Manrope\', sans-serif;letter-spacing:0px;font-weight:500;text-align:left;line-height:1.25;text-shadow:0px 0px 0px transparent;margin:0em 0.5em 0em 0.5em;padding:0px 0px 0px 0px;font-style:normal"><p style="text-align:center; font-weight:700">This is a cluster label, it represents another dimension in the data set</p></div>',
      },
    },
    {
      c1: 'Label 2',
      '~meta': {
        labelWidth: 20,
        labelHeight: 15,
        labelWidthUpdated: 20,
        labelHeightUpdated: 15,
        htmlStr:
          '<div style="font-size:11px;font-family:\'Manrope\', sans-serif;letter-spacing:0px;font-weight:500;text-align:left;line-height:1.25;text-shadow:0px 0px 0px transparent;margin:0em 0.5em 0em 0.5em;padding:0px 0px 0px 0px;font-style:normal"><p style="text-align:center; font-weight:700">This is another cluster field</p></div>',
      },
    },
  ];

  if (grouped) {
    const groupRows = group(chartManager.data, (obj) => obj[aggregationField] as string);
    for (const [key, dataGroup] of groupRows) {
      const range = dataGroup.map((row, idx) => idx * (itemHeight + groupPaddingInnerPx) + groupPaddingOuterPx / 2);
      const groupLabels = dataGroup.map((obj) => obj[labelField] as string);
      chartManager.groupScalesOrdinals[key] = scaleOrdinal().domain(groupLabels).range(range);
    }
  }

  const totalPaddingInnerPx = paddingInnerPx;
  const totalPadding = totalPaddingInnerPx + paddingOuterPx;

  const laneHeights = []; // Label Height
  let dynamicHeightTotal = totalPadding;

  let labelY = paddingOuterPx / 2; // start with paddingOuterPx
  let itemY = paddingOuterPx / 2; // start with paddingOuterPx

  const horizontalItems = [];

  // This should be in ChartManager
  const dotFirstLabelTotalHeight = chartManager.dotFirstLabelHeight + chartManager.dotFirstLabelOffset;
  if (DotTypes.includes(type) && dots.firstLabel.enabled) {
    itemY += dotFirstLabelTotalHeight;
    labelY += dotFirstLabelTotalHeight;
  }

  // Compare label and item heights to set laneHeight (Y positions) on an ordinal scale
  for (const [idx, dataRow] of axisValues.entries()) {
    let laneHeight = 0;

    const { labelStr, labelHeightUpdated, labelHeight } = dataRow['~meta'];
    const _labelHeight = labelHeightUpdated ?? labelHeight;

    const isLabelBigger = enabled && (grouped ? groupHeight < _labelHeight : itemHeight < _labelHeight);

    const itemUnit = grouped ? groupHeight : itemHeight;

    if (labelsAboveBars) itemY += enabled ? _labelHeight : 0;
    else itemY += isLabelBigger ? (_labelHeight - itemUnit) / 2 : 0; // Compare Label height and Item height

    horizontalItems.push({
      label: { y: labelY, height: _labelHeight },
      item: { y: itemY, height: itemHeight },
    });
    // Add Padding + height for next item
    itemY += paddingInnerPx / (n - 1) + itemUnit;

    if (labelsAboveBars) {
      // Add label height and bar/dot height
      laneHeight = enabled ? itemUnit + _labelHeight : itemUnit;
    } else {
      // Compare label height with bar/dot height
      laneHeight = isLabelBigger ? _labelHeight : itemUnit;
      itemY += isLabelBigger ? (_labelHeight - itemUnit) / 2 : 0; // Add other half
    }

    dynamicHeightTotal += laneHeight;
    laneHeights.push(laneHeight);
    labelY += paddingInnerPx / (n - 1) + laneHeight;
  }

  // console.log("horizontalItems", horizontalItems)
  chartManager.labelAvailableHeights = laneHeights;
  chartManager.yScaleOrdinal.range(horizontalItems);

  // Adjust chart height in case dynamic Height
  const offsetTop = chartManager.getOffset('top');
  const offsetBottom = chartManager.getOffset('bottom');
  dynamicHeightTotal += offsetTop + offsetBottom;

  // First Labels on dots
  if (DotTypes.includes(type) && dots.firstLabel.enabled) dynamicHeightTotal += dotFirstLabelTotalHeight;

  // In case Value Axis is enabled
  dynamicHeightTotal += chartManager.xAxisHeight ? chartManager.xAxisHeight : 0;

  // EXPERIMENTAL: Cluster Fields
  // if (false) {
  //   for (const dataRow of clusterLabels) {
  //     dynamicHeightTotal += dataRow['~meta'].labelHeightUpdated;
  //   }
  // }

  chartManager.totalHeight = Math.floor(dynamicHeightTotal);

  /*
    Following code was in the chartManager.updateChartContainerSize(),
    but this function (processOrdinalScale) is again called afterwards in chartManager.renderChart()
    in which the final totalHeight is set.
    Makes sense to set the SVG and Overlay height here, where is finally calculated
  */
  if (adjustWidth && chartManager.totalHeight > chartManager.containerHeight) {
    chartManager.chartWidth -= 20;
    chartManager.containerWidth -= 20;
  }

  updateSVG(chartManager.chart, roundTo(chartManager.containerWidth), roundTo(chartManager.totalHeight));
  updateOverlay(chartManager.overlay, roundTo(chartManager.containerWidth), roundTo(chartManager.totalHeight));

  if (dynamicHeight) {
    chartManager.containerHeight = chartManager.totalHeight - Math.floor(offsetTop) - Math.floor(offsetBottom);
  }
};

export const processLabelAxisQuill = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { labelAxis } = typeSpecific.chart.axis;
  const { grid, dynamicHeight, bars, dots } = typeSpecific.chart;
  const { titleStyle, valueStyle, labelsAboveBars } = labelAxis;

  const dynamicTitleStyle = cloneObject(titleStyle);
  dynamicTitleStyle.size = chartManager.getDynamicFontSize(titleStyle);

  const dynamicLabelStyle = cloneObject(valueStyle);
  dynamicLabelStyle.size = chartManager.getDynamicFontSize(valueStyle);

  let oneLineHeight = dynamicLabelStyle.size + dynamicLabelStyle.size * (fixLineHeight(dynamicLabelStyle.lineheight.value) - 1);

  const titleMarginTop = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'top');
  const titleMarginBottom = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'bottom');
  const titleMarginLeft = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'left');
  const titleMarginRight = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'right');

  // const valueMarginTop = chartManager.getStyleInPx(dynamicValueStyle.margin, 'top');
  // const valueMarginBottom = chartManager.getStyleInPx(dynamicValueStyle.margin, 'bottom');
  // const valueMarginLeft = chartManager.getStyleInPx(dynamicLabelStyle.margin, 'left');
  // const valueMarginRight = chartManager.getStyleInPx(dynamicLabelStyle.margin, 'right');

  // const { margin, padding } = typeSpecific.chart.style;
  // const offsetLeft = chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');
  // const offsetRight = chartManager.getStyleInPx(margin, 'right') + chartManager.getStyleInPx(padding, 'right');

  const offsetLeft = chartManager.getOffset('left');
  const offsetRight = chartManager.getOffset('right');

  // reset label data
  axisValues = [];

  if (!labelAxis.enabled || isPieType(type)) {
    if (isVerticalType(type) || isTimeType(type)) chartManager.updateChartHeight(0);
    if (isHorizontalType(type)) chartManager.updateChartWidth(0);
    if (isPieType(type)) {
      chartManager.updateChartWidth(0);
      chartManager.updateChartHeight(0);
    }

    return;
  }

  const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
  const { dataType, format } = typeSpecific.fields[mapField];

  const oneRow = chartManager.data[0];
  const labelQuill = chartManager.getHTMLandRect(labelAxis.quillValue, dynamicLabelStyle, oneRow, chartManager.content);
  if (labelQuill) {
    oneLineHeight = labelQuill.height;
  }

  // populate label data
  const axisSet = new Set();
  for (const dataRow of chartManager.data) {
    if (axisSet.has(dataRow[mapField])) continue;
    axisSet.add(dataRow[mapField]);
    axisValues.push(dataRow);
  }

  const _customQuillKeys = chartManager.getQuillsIds(labelAxis.customQuills);

  let useTimeScale = false;
  let isSpaceForLabel = true;
  if (isVerticalType(type)) {
    // check if 90° single line > bandwidth and time type -> use timeScaleTicks then
    const bandwidth = chartManager.getBandwidth();
    const availableWidth = bandwidth > 0 ? bandwidth : chartManager.xScale.range()[1] / axisValues.length;
    isSpaceForLabel = availableWidth >= oneLineHeight;

    if (dataType === 'date') {
      let isContinuous = true;
      const { inputFormat } = format.date;
      const momentValues = axisValues.map((obj) => objToMoment(obj[mapField], inputFormat, format.locale));
      if (momentValues.length > 1) {
        let avgDiff = momentValues[1].diff(momentValues[0]);
        for (let idx = 1; idx < momentValues.length; idx++) {
          const curDiff = momentValues[idx].diff(momentValues[idx - 1]);
          if (momentValues[idx].isBefore(momentValues[idx - 1])) isContinuous = false;
          if (curDiff < avgDiff * 0.5 || curDiff > avgDiff * 1.5) isContinuous = false;
          avgDiff = (avgDiff + curDiff) / 2;
        }
      }

      useTimeScale = !isSpaceForLabel && isContinuous;
    }
  }

  if ((isTimeType(type) && dataType === 'date') || useTimeScale) {
    const { inputFormat, dateFormat } = format.date;

    const dynamicLabelStyle = cloneObject(labelAxis.valueStyle);
    dynamicLabelStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

    let estTicks = 3;
    if (labelAxis.autoTicks) {
      const dataRow = chartManager.data[0];
      const labelQuill = chartManager.getHTMLandRect(labelAxis.quillValue, dynamicLabelStyle, dataRow, chartManager.content);
      if (labelQuill) {
        estTicks = Math.max(3, Math.ceil(chartManager.xScale.range()[1] / (labelQuill.width * 2)));
      }
    }

    // grab min max date and build timeline, take 3-5 (default) values
    if (axisValues.length > estTicks) {
      if (labelAxis.smoothScale && chartManager.xScaleTime) {
        const timeStamps = timeScaleTicks(chartManager.xScaleTime, labelAxis.autoTicks ? estTicks : labelAxis.tickInput);
        const lowerInputFormat = inputFormat.toLowerCase();

        const timeSet = new Set<string>();
        const uniqueStamps = [];
        for (const date of timeStamps) {
          const dateStr = momentToStr(date, inputFormat, format.locale);
          if (!timeSet.has(dateStr)) {
            timeSet.add(dateStr);
            uniqueStamps.push(date);
          }
        }

        axisValues = uniqueStamps.map((date) => {
          const cloneRow = cloneObject(chartManager.data[0]);
          cloneRow[mapField] = momentToStr(date, inputFormat, format.locale); // date.format(inputFormat);
          cloneRow['~meta'].moment = date;
          return cloneRow;
        });
      } else {
        axisValues = timeTicks(axisValues, labelAxis.autoTicks ? estTicks : labelAxis.tickCount);
      }
    }

    if (Array.isArray(labelAxis.tickValues)) {
      const userValues = [];
      for (const value of labelAxis.tickValues) {
        const obj = objToMoment(value, dateFormat, format.locale, false);
        if (getMoment().isMoment(obj) && obj.isValid()) userValues.push(obj);
      }

      if (userValues.length > 0) {
        axisValues = userValues.map((date) => {
          const cloneRow = cloneObject(chartManager.data[0]);
          cloneRow[mapField] = momentToStr(date, inputFormat, format.locale); // date.format(inputFormat);
          cloneRow['~meta'].moment = date;
          return cloneRow;
        });
      }
    }

    // axisValues.entries() are the ordered visible axis labels
    for (const [i, dataRow] of axisValues.entries()) {
      const label = dataRow[mapField];
      const labelQuillHTML = chartManager.getQuill(labelAxis.customQuills, label, labelAxis.quillValue);
      const labelQuill = chartManager.getHTMLandRect(labelQuillHTML, dynamicLabelStyle, dataRow, chartManager.content);

      if (labelQuill) {
        dataRow['~meta'].labelStr = labelQuill.html;
        dataRow['~meta'].labelWidth = labelQuill.width;
        dataRow['~meta'].labelHeight = labelQuill.height;
      }
    }
  }

  if (isTimeType(type) && dataType !== 'date') {
    const dynamicLabelStyle = cloneObject(labelAxis.valueStyle);
    dynamicLabelStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

    let estTicks = 3;
    if (labelAxis.autoTicks) {
      const dataRow = chartManager.data[chartManager.data.length - 1];
      const labelQuill = chartManager.getHTMLandRect(labelAxis.quillValue, dynamicLabelStyle, dataRow, chartManager.content);
      if (labelQuill) {
        estTicks = Math.max(3, Math.ceil(chartManager.xScale.range()[1] / (labelQuill.width * 2)));
      }
    }

    // grab min max date and build timeline, take 3-5 (default) values
    if (axisValues.length > estTicks && labelAxis.smoothScale) {
      axisValues = timeTicks(axisValues, labelAxis.autoTicks ? estTicks : labelAxis.tickCount);
    }

    if (Array.isArray(labelAxis.tickValues)) {
      const userValues = [];
      for (const value of labelAxis.tickValues) {
        userValues.push(value);
      }

      if (userValues.length > 0) {
        axisValues = userValues.map((value) => {
          const cloneRow = cloneObject(chartManager.data[0]);
          cloneRow[mapField] = value;
          return cloneRow;
        });
      }
    }

    for (const [i, dataRow] of axisValues.entries()) {
      const label = dataRow[mapField];
      const labelQuillHTML = chartManager.getQuill(labelAxis.customQuills, label, labelAxis.quillValue);
      const labelQuill = chartManager.getHTMLandRect(labelQuillHTML, dynamicLabelStyle, dataRow, chartManager.content);

      if (labelQuill) {
        dataRow['~meta'].labelStr = labelQuill.html;
        dataRow['~meta'].labelWidth = labelQuill.width;
        dataRow['~meta'].labelHeight = labelQuill.height;
      }
    }
  }

  if (isVerticalType(type) || isTimeType(type) || useTimeScale) {
    let axisHeight = 0;

    // we assume equal / linear space usage
    const bandwidth = chartManager.getBandwidth();
    const availableWidth = bandwidth > 0 && !useTimeScale && !isTimeType(type) ? bandwidth : chartManager.xScale.range()[1] / axisValues.length;

    // calculate rotation
    if (labelAxis.autoRotation || labelAxis.minRotation < 0) {
      let rotationRequired = false;
      chartManager.labelRotationDegrees = 0;

      for (const dataRow of axisValues) {
        const rowIdx = axisValues.indexOf(dataRow);
        const { labelStr, labelWidth, labelHeight } = dataRow['~meta'];

        if (labelWidth <= availableWidth) continue;

        let updatedWidth = labelWidth;
        let updatedHeight = labelHeight;

        const rawLabel = labelStr.replace(/<[^>]*>/g, '');
        const match = /\r|\n|\s/.exec(rawLabel);

        if (labelAxis.autoLines) {
          const estLines = Math.min(Math.ceil(chartManager.maxLabelWidth / availableWidth), Math.floor(availableWidth / chartManager.maxLabelHeight));

          const customWidth = match ? Math.ceil((chartManager.maxLabelWidth / estLines) * (estLines > 1 ? 1.25 : 1)) : Math.ceil(labelWidth);

          let { width, height } = chartManager.getElementRect(labelStr, customWidth, undefined, true, undefined, false, false);

          const { width: minWidth, height: maxHeight } = chartManager.getElementRect(labelStr, customWidth, undefined, true, undefined, false, true);

          if (width > height * 1.5) {
            width = Math.max(width, minWidth);
            updatedWidth = width;
            updatedHeight = height;

            dataRow['~meta'].labelWidthUpdated = width;
            dataRow['~meta'].labelHeightUpdated = height;
          }

          // console.log(
          //   dataRow[mapField],
          //   'lines',
          //   estLines,
          //   'width',
          //   customWidth,
          //   'available',
          //   roundTo(availableWidth),
          //   'w/h',
          //   updatedWidth,
          //   updatedHeight,
          //   'min/max',
          //   minWidth,
          //   maxHeight
          // );
        } else {
          const customWidth = match ? Math.ceil((labelWidth / labelAxis.maxLines) * (labelAxis.maxLines > 1 ? 1.33 : 1)) : Math.ceil(labelWidth);

          let { width, height } = chartManager.getElementRect(labelStr, customWidth, undefined, false, undefined, false);
          const { width: minWidth, height: maxHeight } = chartManager.getElementRect(labelStr, customWidth, undefined, false, undefined, false, true);

          if (width > height) {
            width = Math.max(width, minWidth);
            updatedWidth = width;
            updatedHeight = height;

            dataRow['~meta'].labelWidthUpdated = width;
            dataRow['~meta'].labelHeightUpdated = height;
          }

          // console.log("🚀 ~ labelHeightUpdated", dataRow['~meta'].labelHeightUpdated)
          // console.log(
          //   dataRow[mapField],
          //   'lines',
          //   labelAxis.maxLines,
          //   'width',
          //   customWidth,
          //   'available',
          //   roundTo(availableWidth),
          //   'w/h',
          //   updatedWidth,
          //   updatedHeight,
          //   'min/max',
          //   minWidth,
          //   maxHeight
          // );
        }

        let targetWidth = availableWidth;
        if (labelAxis.smoothScale && useTimeScale) {
          if (rowIdx === 0) targetWidth = availableWidth * 0.67;
          if (rowIdx === axisValues.length - 1) targetWidth = availableWidth * 0.67;
        }

        if (updatedWidth > targetWidth + 1) {
          rotationRequired = true;
        }
      }

      if (rotationRequired) {
        for (const dataRow of axisValues) {
          const rowIdx = axisValues.indexOf(dataRow);
          const { labelStr, labelWidth, labelHeight, labelWidthUpdated, labelHeightUpdated } = dataRow['~meta'];
          let targetWidth = availableWidth;
          if (labelAxis.smoothScale || useTimeScale) {
            if (rowIdx === 0) targetWidth = availableWidth * 0.67;
            if (rowIdx === axisValues.length - 1) targetWidth = availableWidth * 0.67;
          }

          if ((labelHeightUpdated ?? labelHeight) > targetWidth) {
            chartManager.labelRotationDegrees = 90;
            break;
          }

          // const estimatedRotation = (Math.asin((labelHeightUpdated ?? labelHeight) / targetWidth) * 180) / Math.PI;
          const estimatedRotation = (Math.acos(targetWidth / Math.max(targetWidth, labelWidthUpdated ?? labelWidth)) * 180) / Math.PI;
          chartManager.labelRotationDegrees = clamp(Math.max(chartManager.labelRotationDegrees, estimatedRotation), 22.5, 90); // 11.25, 22.5
          // console.log(dataRow[mapField], labelWidth, labelHeight, labelWidthUpdated, labelHeightUpdated, estimatedRotation);
        }
      }
    } else {
      chartManager.labelRotationDegrees = Math.max(0, labelAxis.minRotation);
    }

    // calculate height of final rotation
    for (const dataRow of axisValues) {
      const { labelStr, labelWidth, labelHeight, labelWidthUpdated, labelHeightUpdated } = dataRow['~meta'];
      const actualWidth = labelWidthUpdated ?? labelWidth;
      const actualHeight = labelHeightUpdated ?? labelHeight;
      if (!labelWidth || !labelHeight) continue;

      const rotation = (chartManager.labelRotationDegrees / 180) * Math.PI;
      const rotatedWidth = Math.sin(rotation) * actualWidth;
      const rotatedHeight = Math.cos(rotation) * actualHeight;
      const rotatedLabelSize = rotatedWidth + rotatedHeight + Math.sin(rotation) * 10;

      // console.log(dataRow[mapField], rotatedWidth, rotatedHeight, labelWidth, labelHeight, chartManager.labelRotationDegrees);
      axisHeight = Math.max(axisHeight, rotatedLabelSize);
    }

    chartManager.xAxisHeightQuill = axisHeight;

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartWidth - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);

      const lineHeight = (textSplit.lines.length - 1) * dynamicTitleStyle.size * (fixLineHeight(dynamicTitleStyle.lineheight.value) - 1);
      const textHeight = textSplit.lines.length * dynamicTitleStyle.size + lineHeight;
      const totalHeight = textHeight + titleMarginTop + titleMarginBottom;

      axisHeight += totalHeight;
    }

    // console.log('rotation', chartManager.labelRotationDegrees, 'axisHeight', axisHeight);

    // console.log('[labelAxisQuill]', 'height', axisHeight);
    chartManager.updateChartHeight(axisHeight);
  }

  if (isHorizontalType(type)) {
    let axisWidth = 0;
    const bandwidth = chartManager.getBandwidth();
    const availableHeight = (bandwidth > 0 ? bandwidth : chartManager.yScale.range()[1] / axisValues.length) + 1;
    const isSpaceForLabel = availableHeight >= dynamicLabelStyle.size;
    // const isSpaceForLabel = true
    const maxLines = Math.max(1, Math.floor(availableHeight / dynamicLabelStyle.size));

    chartManager.labelAvailableHeight = availableHeight;

    // console.log('isHorizontalType', availableHeight, isSpaceForLabel, dynamicLabelStyle.size);

    if (isSpaceForLabel) {
      if (!labelAxis.labelsAboveBars) {
        const availableWidth = (chartManager.containerWidth - offsetLeft - offsetRight) * (labelAxis.maxWidth / 100);

        if (labelAxis.autoLines) {
          for (const dataRow of axisValues) {
            const rowIdx = axisValues.indexOf(dataRow);
            const { labelStr, labelWidth, labelHeight } = dataRow['~meta'];
            const { width, height } = chartManager.getElementRect(labelStr, availableWidth, undefined, maxLines > 1, undefined, false, false);
            // console.log('availableWidth', availableWidth, dataRow[mapField], width, height);

            dataRow['~meta'].labelWidthUpdated = width;
            dataRow['~meta'].labelHeightUpdated = height;

            axisWidth = Math.max(axisWidth, Math.ceil(width));
          }
        } else {
          for (const dataRow of axisValues) {
            const rowIdx = axisValues.indexOf(dataRow);
            const { labelStr, labelWidth, labelHeight } = dataRow['~meta'];

            axisWidth = Math.max(axisWidth, Math.ceil(labelWidth));
          }

          axisWidth = Math.ceil((axisWidth / labelAxis.maxLines) * (labelAxis.maxLines > 1 ? 1.5 : 1));
        }

        chartManager.labelTextMaxWidth = axisWidth;
      } else {
        const availableWidth = chartManager.xScale(chartManager.xScale.domain()[1]) - chartManager.xScale(0);
        const _width = availableWidth * (labelAxis.maxWidth / 100);
        // Why ? Ins't labels-above suppose to take ALL space ?
        // @gregor, I may be wrong but labels-above are allow to take spaces as the whole chart
        const chartWidth = chartManager.chartWidth;

        let maxWidth = 0;
        for (const dataRow of axisValues) {
          const rowIdx = axisValues.indexOf(dataRow);
          const { labelStr, labelWidth, labelHeight } = dataRow['~meta'];
          const { width, height } = chartManager.getElementRect(labelStr, chartWidth, undefined, false, false, false);

          dataRow['~meta'].labelWidthUpdated = width;
          dataRow['~meta'].labelHeightUpdated = height;

          maxWidth = Math.max(maxWidth, Math.ceil(width));
        }

        chartManager.labelTextMaxWidth = maxWidth;
      }
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartHeight - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);

      const lineHeight = (textSplit.lines.length - 1) * dynamicTitleStyle.size * (fixLineHeight(dynamicTitleStyle.lineheight.value) - 1);
      const textHeight = textSplit.lines.length * dynamicTitleStyle.size + lineHeight;
      const totalHeight = textHeight + titleMarginTop + titleMarginBottom;

      axisWidth += totalHeight;
    }

    // console.log('[labelAxisQuill]', 'width', axisWidth);
    axisWidth += 1; // prevent font from sticking to chart
    chartManager.updateChartWidth(axisWidth);
  }
};

export const renderLabelAxisQuill = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { labelAxis } = typeSpecific.chart.axis;
  const { dynamicHeight, bars, dots } = typeSpecific.chart;

  const { margin, padding } = typeSpecific.chart.style;
  const marginLeft = chartManager.getStyleInPx(margin, 'left');
  const marginTop = chartManager.getStyleInPx(margin, 'top');
  const marginBottom = chartManager.getStyleInPx(margin, 'bottom');
  const paddingLeft = chartManager.getStyleInPx(padding, 'left');
  const paddingBottom = chartManager.getStyleInPx(padding, 'bottom') + chartManager.dataLabelOffset.bottom;

  // const offsetTop = chartManager.getStyleInPx(margin, 'top') + chartManager.getStyleInPx(padding, 'top');
  // const offsetLeft = chartManager.getStyleInPx(margin, 'left') + chartManager.getStyleInPx(padding, 'left');

  const offsetLeft = chartManager.getOffset('left');
  const offsetRight = chartManager.getOffset('right');

  const dynamicTitleStyle = cloneObject(labelAxis.titleStyle);
  dynamicTitleStyle.size = chartManager.getDynamicFontSize(labelAxis.titleStyle);

  const dynamicLabelStyle = cloneObject(labelAxis.valueStyle);
  dynamicLabelStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

  let oneLineHeight = dynamicLabelStyle.size + dynamicLabelStyle.size * (fixLineHeight(dynamicLabelStyle.lineheight.value) - 1);

  const titleMarginTop = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'top');
  const titleMarginBottom = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'bottom');
  const titleMarginLeft = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'left');
  const titleMarginRight = chartManager.getStyleInPx(dynamicTitleStyle.margin, 'right');

  // const valueMarginTop = chartManager.getStyleInPx(dynamicLabelStyle.margin, 'top');
  // const valueMarginBottom = chartManager.getStyleInPx(dynamicLabelStyle.margin, 'bottom');
  // const valueMarginLeft = chartManager.getStyleInPx(dynamicLabelStyle.margin, 'left');
  // const valueMarginRight = chartManager.getStyleInPx(dynamicLabelStyle.margin, 'right');

  const data = [];
  const dataD3: D3TextData[] = [];
  if (!labelAxis.enabled) {
    d3renderText(chartManager.chart.element, dataD3, '.labelAxis');
    overlayText(chartManager.overlay, data, '.overlay-labelAxis');
    return;
  }

  const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
  const { dataType, format } = typeSpecific.fields[mapField];

  const oneRow = chartManager.data[0];
  const labelQuill = chartManager.getHTMLandRect(labelAxis.quillValue, dynamicLabelStyle, oneRow, chartManager.content);
  if (labelQuill) {
    oneLineHeight = labelQuill.height;
  }

  if (isVerticalType(type) || isTimeType(type)) {
    const { sorting } = typeSpecific.chart;
    const isSortedByTime = !sorting.enabled || sorting.sortFields.length === 0 || sorting.sortFields[0] === mapField;

    const isContinuous = true;
    let useTimeScale = false;
    let isSpaceForLabel = true;
    if (isVerticalType(type)) {
      // check if 90° single line > bandwidth and time type -> use timeScaleTicks then
      const bandwidth = chartManager.getBandwidth();
      const availableWidth = bandwidth > 0 ? bandwidth : chartManager.xScale.range()[1] / axisValues.length;
      isSpaceForLabel = availableWidth >= oneLineHeight;

      if (dataType === 'date') {
        let isContinuous = true;
        const { inputFormat } = format.date;
        const momentValues = axisValues.map((obj) => objToMoment(obj[mapField], inputFormat, format.locale));
        if (momentValues.length > 1) {
          let avgDiff = momentValues[1].diff(momentValues[0]);
          for (let idx = 1; idx < momentValues.length; idx++) {
            const curDiff = momentValues[idx].diff(momentValues[idx - 1]);
            if (momentValues[idx].isBefore(momentValues[idx - 1])) isContinuous = false;
            if (curDiff < avgDiff * 0.5 || curDiff > avgDiff * 1.5) isContinuous = false;
            avgDiff = (avgDiff + curDiff) / 2;
          }
        }

        useTimeScale = !isSpaceForLabel && isContinuous;
      }
    }

    const bandwidth = chartManager.getBandwidth();
    const availableWidth = bandwidth > 0 && !useTimeScale && !isTimeType(type) ? bandwidth : chartManager.xScale.range()[1] / axisValues.length;

    useTimeScale = useTimeScale || (isTimeType(type) && dataType === 'date');
    const isTimeValueAndType = dataType === 'date' && ((isSortedByTime && isContinuous) || isTimeType(type)); // this takes render pipeline area/line sorting into account

    const hasCustomValues = Array.isArray(labelAxis.tickValues) && labelAxis.tickValues.length > 0;

    for (const dataRow of axisValues) {
      const rowIdx = axisValues.indexOf(dataRow);
      const obj = dataRow[mapField];
      const { moment: metaMoment, labelStr, labelWidth, labelHeight, labelWidthUpdated, labelHeightUpdated } = dataRow['~meta'];
      // console.log("🚀 ~ labelStr", labelStr)
      if (!labelWidth || !labelHeight) continue;

      const actualWidth = labelWidthUpdated ?? labelWidth;
      const actualHeight = labelHeightUpdated ?? labelHeight;

      const relativeX = useTimeScale ? chartManager.xScaleTime(metaMoment) : chartManager.xScale(obj);
      let x = relativeX; // chartManager.yAxisWidth
      const y = chartManager.chartHeight + paddingBottom;
      if (Number.isNaN(x) || Number.isNaN(y)) continue;

      const rotation = (chartManager.labelRotationDegrees / 180) * Math.PI;
      const isRotated = chartManager.labelRotationDegrees > 0;

      // console.log(rowIdx, labelStr, actualWidth, actualHeight, x, y);

      // console.log('render', rowIdx, obj, 'deg', chartManager.labelRotationDegrees, actualWidth, actualHeight);

      if (isRotated) {
        x += bandwidth / 2 - actualWidth;
        x -= (Math.sin(rotation) * actualHeight) / 2;
      }

      if (useTimeScale && !isRotated && !hasCustomValues) {
        // if (rowIdx === 0 && !labelAxis.smoothScale) {
        //   x -= availableWidth / 2;
        // }

        if (rowIdx > 0 && rowIdx < axisValues.length - 1) {
          x -= availableWidth / 2;
        }

        if (rowIdx === axisValues.length - 1) {
          x -= availableWidth;
        }
      }

      if (hasCustomValues) {
        x -= availableWidth / 2;
      }

      // alignment
      const origin = isRotated ? 'right top 0px' : 'center center 0px';
      let alignment = isRotated ? 'center-right' : 'top-center';

      if (!hasCustomValues) {
        if (useTimeScale && rowIdx === 0) alignment = 'center-left';
        if (useTimeScale && rowIdx === axisValues.length - 1) alignment = 'center-right';
      }

      const { justifyContent, alignItems } = alignmentToFlex(alignment, 'top'); // labelAxis.valueAlignment

      const rotatedWidth = isRotated ? actualWidth : availableWidth; // Math.cos(rotation) * bandWidth + Math.sin(rotation) * chartManager.xAxisHeightQuill;
      const rotatedHeight = actualHeight; // chartManager.xAxisHeightQuill; // Math.sin(rotation) * bandWidth + Math.cos(rotation) * chartManager.xAxisHeightQuill;

      const labelData = {
        html: labelStr,
        x: roundTo(x),
        y: roundTo(y),
        class: { rotate: isRotated },
        width: roundTo(rotatedWidth) + 1,
        height: roundTo(rotatedHeight),
        color: color2css(labelAxis.valueStyle.color),
        callbacks: {},
        justifyContent,
        alignItems,
        transform: `translate(${roundTo(x)}px, ${roundTo(y)}px) rotate(-${chartManager.labelRotationDegrees}deg)`,
        transformOrigin: origin,
        // writingMode: textRotate ? 'vertical-rl' : undefined,
        // transform: textRotate ? `translate(${xO}px, ${yO}px) rotate(180deg)` : `translate(${xO}px, ${yO}px)`,
        // transformForce: textRotate ? `rotate(180deg)` : ``,
        // animate: {
        //   enabled: animation.enabled,
        //   delay: animation.delay,
        //   duration: animation.duration,
        //   width: roundTo(width),
        //   height: roundTo(valueInside ? height : availableHeight - height),
        //   writingMode: textRotate ? 'vertical-rl' : undefined,
        //   transform: textRotate ? `translate(${xA}px, ${yA}px) rotate(180deg)` : `translate(${xA}px, ${yA}px)`,
        // },
      };

      // console.log(labelData);

      data.push(labelData);
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartWidth - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);
      const fontStyle = String(dynamicTitleStyle.weight).includes('i') ? 'italic' : undefined;

      let idx = textSplit.lines.length - 1;
      for (const line of textSplit.lines) {
        const dy = `${idx * -fixLineHeight(dynamicTitleStyle.lineheight.value) - 0.25}em`;
        dataD3.push({
          text: line,
          color: color2css(dynamicTitleStyle.color),
          fontSize: roundTo(dynamicTitleStyle.size),
          fontStyle,
          fontWeight: String(dynamicTitleStyle.weight).replace('i', ''),
          fontFamily: dynamicTitleStyle.fontFamily,
          x: roundTo(offsetLeft + chartManager.yAxisWidth + chartManager.chartWidth / 2),
          y: roundTo(chartManager.chart.height - marginBottom - titleMarginBottom),
          dy,
          anchor: 'middle',
        });

        idx--;
      }
    }
  }

  if (isHorizontalType(type)) {
    for (const dataRow of axisValues) {
      const rowIdx = axisValues.indexOf(dataRow);
      const obj = dataRow[mapField];
      const { moment, labelStr, labelWidth, labelHeight, labelWidthUpdated, labelHeightUpdated } = dataRow['~meta'];
      if (!labelWidth || !labelHeight) continue;

      const actualWidth = labelWidthUpdated ?? labelWidth;
      const actualHeight = labelHeightUpdated ?? labelHeight;

      let x = -chartManager.labelTextMaxWidth - paddingLeft - chartManager.dataLabelOffset.left;
      // let y = chartManager.yScale(obj);
      const { label } = chartManager.yScaleOrdinal(obj);
      const { y } = label;

      let width = chartManager.labelTextMaxWidth;
      let height = roundTo(chartManager.labelAvailableHeight);

      let justifyContent = 'flex-end';
      let alignItems = 'center';

      // const availableHeight = bandwidth > 0 ? bandwidth : chartManager.yScale.range()[1] / axisValues.length;
      // const isSpaceForLabel = availableHeight >= dynamicLabelStyle.size;
      // const maxLines = Math.floor(availableHeight / dynamicLabelStyle.size);

      if (labelAxis.labelsAboveBars) {
        const availableWidth = chartManager.xScale(chartManager.xScale.domain()[1]) - chartManager.xScale(0);

        x = chartManager.xScale(0);
        // width = availableWidth * (labelAxis.maxWidth / 100);
        width = chartManager.chartWidth;
        justifyContent = 'flex-start';
        alignItems = 'flex-start';
      }

      height = chartManager.labelAvailableHeights[rowIdx];

      const labelData = {
        html: labelStr,
        x: roundTo(x),
        y: roundTo(y),
        class: { rotate: false },
        width: roundTo(width),
        height,
        color: color2css(labelAxis.valueStyle.color),
        callbacks: {},
        // justifyContent,
        alignItems,
        // flex: '1 1 auto',
        transform: `translate(${roundTo(x)}px, ${roundTo(y)}px)`,
        // transformOrigin: origin,
        // writingMode: textRotate ? 'vertical-rl' : undefined,
        // transform: textRotate ? `translate(${xO}px, ${yO}px) rotate(180deg)` : `translate(${xO}px, ${yO}px)`,
        // transformForce: textRotate ? `rotate(180deg)` : ``,
        // animate: {
        //   enabled: animation.enabled,
        //   delay: animation.delay,
        //   duration: animation.duration,
        //   width: roundTo(width),
        //   height: roundTo(valueInside ? height : availableHeight - height),
        //   writingMode: textRotate ? 'vertical-rl' : undefined,
        //   transform: textRotate ? `translate(${xA}px, ${yA}px) rotate(180deg)` : `translate(${xA}px, ${yA}px)`,
        // },
        // outline: '1px solid #d97706', // DEBUG
      };

      data.push(labelData);
    }

    if (labelAxis.title && labelAxis.title.length > 0) {
      const maxWidth = chartManager.chartHeight - titleMarginLeft - titleMarginRight;
      const textSplit = chartManager.splitTextToWidth(labelAxis.title, maxWidth, dynamicTitleStyle);

      const x = marginLeft + titleMarginTop;
      const y = marginTop + chartManager.chartHeight / 2;

      const fontStyle = String(dynamicTitleStyle.weight).includes('i') ? 'italic' : undefined;

      let idx = 0;
      for (const line of textSplit.lines) {
        const dy = `${idx * fixLineHeight(dynamicTitleStyle.lineheight.value) + 0.75}em`;
        // console.log('isHorizontalType [dataD3]', line);
        dataD3.push({
          text: line,
          color: color2css(dynamicTitleStyle.color),
          fontSize: roundTo(dynamicTitleStyle.size),
          fontStyle,
          fontWeight: String(dynamicTitleStyle.weight).replace('i', ''),
          fontFamily: dynamicTitleStyle.fontFamily,
          x: roundTo(x),
          y: roundTo(y),
          dy,
          anchor: 'middle',
          transform: `rotate(-90 ${roundTo(x)} ${roundTo(y)})`,
        });

        idx++;
      }
    }
  }

  d3renderText(chartManager.chart.element, dataD3, '.labelAxis');
  overlayText(chartManager.overlay, data, '.overlay-labelAxis');
};
